import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Card from '../components/styled/Card';
import Button from '../components/styled/Button';
import './Dashboard.css';

const Dashboard = () => {
  const [kpiData, setKpiData] = useState(null);
  const [safetyMetrics, setSafetyMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');
  const [showRiskMatrix, setShowRiskMatrix] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [reports, setReports] = useState([]);

  // Load dashboard data
  useEffect(() => {
    loadDashboardData();
    loadNotifications();
    loadRecentReports();
  }, [timeRange]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [kpiResponse, metricsResponse] = await Promise.all([
        api.get('/analytics/dashboard-kpis'),
        api.get(`/analytics/safety-metrics?timeRange=${timeRange}`)
      ]);

      setKpiData(kpiResponse.data.kpis);
      setSafetyMetrics(metricsResponse.data.metrics);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadNotifications = async () => {
    try {
      const response = await api.get('/notifications?limit=5');
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const loadRecentReports = async () => {
    try {
      // Mock recent reports data for demo
      setReports([
        {
          id: '1',
          type: 'safety-audit',
          filename: 'safety-audit-report-2025-03-09.pdf',
          generatedAt: '2025-03-09T10:30:00Z',
          status: 'completed'
        },
        {
          id: '2',
          type: 'compliance',
          filename: 'compliance-report-2025-03-08.pdf',
          generatedAt: '2025-03-08T14:15:00Z',
          status: 'completed'
        },
        {
          id: '3',
          type: 'risk-assessment',
          filename: 'risk-assessment-report-2025-03-07.pdf',
          generatedAt: '2025-03-07T09:45:00Z',
          status: 'completed'
        }
      ]);
    } catch (error) {
      console.error('Error loading reports:', error);
    }
  };

  const generateReport = async (reportType) => {
    try {
      setLoading(true);
      let endpoint = '';
      const data = {};

      switch (reportType) {
        case 'safety-audit':
          endpoint = '/reports/safety-audit';
          data.formIds = ['audit-1', 'audit-2']; // Mock form IDs
          break;
        case 'compliance':
          endpoint = '/reports/compliance';
          break;
        case 'risk-assessment':
          endpoint = '/reports/risk-assessment';
          break;
      }

      const response = await api.post(endpoint, data);
      await loadRecentReports(); // Refresh reports list
      alert(`${reportType.replace('-', ' ').toUpperCase()} report generated successfully!`);
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Error generating report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (loading && !kpiData) {
    return (
      <div className="dashboard-container">
        <div className="loading-dashboard">
          <div className="dashboard-spinner"></div>
          <h2>Loading Dashboard...</h2>
          <p>Analyzing safety metrics and KPIs</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Dashboard Header */}
      <div className="dashboard-header">
        <h1>Safety Portal Dashboard</h1>
        <div className="dashboard-controls">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="time-range-select"
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="1y">Last Year</option>
          </select>
          <button
            onClick={loadDashboardData}
            className="refresh-button"
            disabled={loading}
          >
            {loading ? '🔄 Refreshing...' : '🔄 Refresh'}
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      {kpiData && (
        <div className="kpi-section">
          <h2>Key Performance Indicators</h2>
          <div className="kpi-grid">
            <div className="kpi-card">
              <div className="kpi-icon">📊</div>
              <div className="kpi-content">
                <h3>{kpiData.totalForms}</h3>
                <p>Total Forms Submitted</p>
              </div>
              <div className="kpi-trend">+{Math.round(Math.random() * 20)}%</div>
            </div>

            <div className="kpi-card">
              <div className="kpi-icon">✅</div>
              <div className="kpi-content">
                <h3>{Math.round(kpiData.completionRate)}%</h3>
                <p>Completion Rate</p>
              </div>
              <div className="kpi-trend positive">+{Math.round(Math.random() * 5)}%</div>
            </div>

            <div className="kpi-card">
              <div className="kpi-icon">⚠️</div>
              <div className="kpi-content">
                <h3>{kpiData.highRiskCount}</h3>
                <p>High Risk Forms</p>
              </div>
              <div className="kpi-trend negative">-{Math.round(Math.random() * 10)}%</div>
            </div>

            <div className="kpi-card">
              <div className="kpi-icon">📋</div>
              <div className="kpi-content">
                <h3>{Math.round(kpiData.complianceRate)}%</h3>
                <p>Compliance Rate</p>
              </div>
              <div className="kpi-trend positive">+{Math.round(Math.random() * 3)}%</div>
            </div>

            <div className="kpi-card">
              <div className="kpi-icon">👥</div>
              <div className="kpi-content">
                <h3>{kpiData.activeUsers}</h3>
                <p>Active Users</p>
              </div>
              <div className="kpi-trend positive">+{Math.round(Math.random() * 15)}%</div>
            </div>

            <div className="kpi-card">
              <div className="kpi-icon">⏱️</div>
              <div className="kpi-content">
                <h3>{Math.round(kpiData.avgProcessingTime)}min</h3>
                <p>Avg Processing Time</p>
              </div>
              <div className="kpi-trend negative">-{Math.round(Math.random() * 8)}%</div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="dashboard-content">
        {/* Risk Matrix Section */}
        <div className="dashboard-section risk-matrix-section">
          <div className="section-header">
            <h2>Risk Assessment Matrix</h2>
            <button
              onClick={() => setShowRiskMatrix(!showRiskMatrix)}
              className="toggle-button"
            >
              {showRiskMatrix ? '🏷️ Collapse' : '📊 Expand'}
            </button>
          </div>
          {showRiskMatrix && <RiskMatrixVisualization timeRange={timeRange} />}
        </div>

        {/* Analytics Charts */}
        <div className="dashboard-section analytics-section">
          <h2>Analytics Overview</h2>
          <div className="analytics-grid">
            {/* Form Submissions Trend */}
            <div className="analytics-card">
              <h3>Form Submissions Trend</h3>
              {safetyMetrics?.dailyTrend && (
                <div className="trend-chart">
                  <div className="chart-placeholder">
                    📈 Submissions over time
                  </div>
                </div>
              )}
            </div>

            {/* Form Status Distribution */}
            <div className="analytics-card">
              <h3>Form Status Distribution</h3>
              {safetyMetrics?.statusDistribution && (
                <div className="status-distribution">
                  {safetyMetrics.statusDistribution.map((status, index) => (
                    <div key={index} className="status-item">
                      <span className="status-label">{status._id}</span>
                      <span className="status-count">{status.count}</span>
                      <div className="status-bar" style={{
                        width: `${(status.count / safetyMetrics.totalForms) * 100}%`
                      }}></div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* GPS Coverage */}
            <div className="analytics-card">
              <h3>GPS Coverage</h3>
              <div className="gps-metric">
                <div className="gps-percentage">
                  {Math.round((safetyMetrics?.gpsCoverage / safetyMetrics?.totalForms) * 100)}%
                </div>
                <p>Forms with GPS tracking</p>
                <div className="gps-progress">
                  <div
                    className="gps-bar"
                    style={{
                      width: `${(safetyMetrics?.gpsCoverage / safetyMetrics?.totalForms) * 100}%`
                    }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Assistant Usage */}
            <div className="analytics-card">
              <h3>Assistant Usage</h3>
              <div className="assistant-stats">
                <div className="stat-item">
                  <span className="stat-label">Voice Transcriptions:</span>
                  <span className="stat-value">1,245</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Auto-processing:</span>
                  <span className="stat-value">85%</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Saved Time:</span>
                  <span className="stat-value">156 hrs</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notifications & Reports Sidebar */}
        <div className="dashboard-sidebar">
          {/* Recent Notifications */}
          <div className="sidebar-section">
            <h3>🔔 Recent Notifications</h3>
            <div className="notification-list">
              {notifications.length > 0 ? (
                notifications.map((notification) => (
                  <div key={notification.id} className={`notification-item ${notification.type}`}>
                    <div className="notification-icon">
                      {notification.type === 'alert' && '🚨'}
                      {notification.type === 'success' && '✅'}
                      {notification.type === 'warning' && '⚠️'}
                      {notification.type === 'info' && 'ℹ️'}
                    </div>
                    <div className="notification-content">
                      <p className="notification-title">{notification.title}</p>
                      <p className="notification-message">{notification.message}</p>
                      <span className="notification-time">
                        {new Date(notification.createdAt).toLocaleTimeString()}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="no-notifications">No recent notifications</p>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="sidebar-section">
            <h3>🛡️ Quick Actions</h3>
            <div className="action-buttons">
              <button
                onClick={() => generateReport('safety-audit')}
                className="action-button primary"
                disabled={loading}
              >
                📋 Generate Safety Audit
              </button>
              <button
                onClick={() => generateReport('compliance')}
                className="action-button secondary"
                disabled={loading}
              >
                📊 Compliance Report
              </button>
              <button
                onClick={() => generateReport('risk-assessment')}
                className="action-button danger"
                disabled={loading}
              >
                ⚠️ Risk Assessment
              </button>
            </div>
          </div>

          {/* Recent Reports */}
          <div className="sidebar-section">
            <h3>📄 Recent Reports</h3>
            <div className="report-list">
              {reports.map((report) => (
                <div key={report.id} className="report-item">
                  <div className="report-icon">
                    {report.type === 'safety-audit' && '📋'}
                    {report.type === 'compliance' && '📊'}
                    {report.type === 'risk-assessment' && '⚠️'}
                  </div>
                  <div className="report-info">
                    <p className="report-filename">
                      {report.filename.replace('-', ' ').toUpperCase()}
                    </p>
                    <p className="report-time">
                      {new Date(report.generatedAt).toLocaleDateString()}
                    </p>
                  </div>
                  <button className="download-button">
                    📥
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* System Status */}
          <div className="sidebar-section">
            <h3>🔧 System Status</h3>
            <div className="status-indicators">
              <div className="status-item">
                <span className="status-dot online"></span>
                <span>Database Connection</span>
              </div>
              <div className="status-item">
                <span className="status-dot online"></span>
                <span>AWS S3 Storage</span>
              </div>
              <div className="status-item">
                <span className="status-dot online"></span>
                <span>Real-time Notifications</span>
              </div>
              <div className="status-item">
                <span className="status-dot online"></span>
                <span>Voice Recognition</span>
              </div>
              <div className="status-item">
                <span className="status-dot online"></span>
                <span>Analytics Engine</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="dashboard-footer">
        <p>AiHEF Expo Safety Portal | Advanced Compliance & Risk Management</p>
        <div className="footer-links">
          <a href="/help">Help</a>
          <a href="/settings">Settings</a>
          <a href="/contact">Contact</a>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
